export const htmlSanitizerPolicy = {
  allowedTags: [
    "a",
    "b",
    "blockquote",
    "br",
    "code",
    "del",
    "div",
    "em",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "i",
    "img",
    "li",
    "mark",
    "ol",
    "p",
    "pre",
    "small",
    "span",
    "strong",
    "table",
    "tbody",
    "td",
    "tfoot",
    "th",
    "thead",
    "tr",
    "u",
    "ul",
  ],
  blockedTags: [
    "base",
    "button",
    "embed",
    "form",
    "iframe",
    "input",
    "link",
    "math",
    "meta",
    "object",
    "option",
    "script",
    "select",
    "style",
    "svg",
    "template",
    "textarea",
  ],
  globalAttributes: ["aria-label", "dir", "lang", "title"],
  tagAttributes: {
    a: ["href", "rel", "target", "title"],
    img: ["alt", "decoding", "height", "loading", "src", "width"],
    td: ["colspan", "rowspan"],
    th: ["colspan", "rowspan", "scope"],
  },
  safeTargets: ["_blank", "_parent", "_self", "_top"],
  safeTones: ["danger", "muted", "primary", "success", "warning"],
} as const;

const allowedTags = new Set<string>(htmlSanitizerPolicy.allowedTags);
const blockedTags = new Set<string>(htmlSanitizerPolicy.blockedTags);
const globalAttributes = new Set<string>(htmlSanitizerPolicy.globalAttributes);

const tagAttributes: Record<string, Set<string>> = Object.fromEntries(
  Object.entries(htmlSanitizerPolicy.tagAttributes).map(([tag, attributes]) => [
    tag,
    new Set<string>(attributes),
  ]),
);

const safeTargets = new Set<string>(htmlSanitizerPolicy.safeTargets);
const safeTones = new Set<string>(htmlSanitizerPolicy.safeTones);

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function sanitizeUrl(value: string, attribute: "href" | "src") {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  try {
    const base =
      typeof window === "undefined"
        ? import.meta.env.VITE_SITE_URL || "https://example.com"
        : window.location.origin;
    const url = new URL(trimmed, base);
    const allowedProtocols =
      attribute === "href"
        ? new Set(["http:", "https:", "mailto:", "tel:"])
        : new Set(["http:", "https:"]);

    if (!allowedProtocols.has(url.protocol)) {
      return null;
    }

    return trimmed;
  } catch {
    return null;
  }
}

function unwrapElement(element: Element) {
  const parent = element.parentNode;

  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }

  parent.removeChild(element);
}

function sanitizeAttributes(element: Element, tagName: string) {
  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase();
    const allowed =
      name === "data-tone" ||
      globalAttributes.has(name) ||
      tagAttributes[tagName]?.has(name);

    if (
      name.startsWith("on") ||
      name === "style" ||
      name === "srcdoc" ||
      !allowed
    ) {
      element.removeAttribute(attribute.name);
      continue;
    }

    if (name === "href" || name === "src") {
      const safeUrl = sanitizeUrl(attribute.value, name);

      if (!safeUrl) {
        element.removeAttribute(attribute.name);
      } else {
        element.setAttribute(name, safeUrl);
      }
    }

    if (name === "target" && !safeTargets.has(attribute.value)) {
      element.removeAttribute(attribute.name);
    }

    if (name === "data-tone" && !safeTones.has(attribute.value)) {
      element.removeAttribute(attribute.name);
    }
  }

  if (tagName === "a" && element.getAttribute("target") === "_blank") {
    element.setAttribute("rel", "noopener noreferrer");
  }

  if (tagName === "img") {
    element.setAttribute("loading", "lazy");
    element.setAttribute("decoding", "async");
  }
}

function sanitizeNode(node: Node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    node.parentNode?.removeChild(node);
    return;
  }

  const element = node as Element;
  const tagName = element.tagName.toLowerCase();

  if (blockedTags.has(tagName)) {
    element.remove();
    return;
  }

  if (!allowedTags.has(tagName)) {
    Array.from(element.childNodes).forEach(sanitizeNode);
    unwrapElement(element);
    return;
  }

  sanitizeAttributes(element, tagName);
  Array.from(element.childNodes).forEach(sanitizeNode);
}

export function sanitizeHtml(html?: string | null) {
  if (!html) {
    return "";
  }

  if (typeof document === "undefined") {
    return escapeHtml(html);
  }

  const template = document.createElement("template");
  template.innerHTML = html;
  Array.from(template.content.childNodes).forEach(sanitizeNode);

  return template.innerHTML;
}

export function sanitizedHtml(html?: string | null) {
  return {
    __html: sanitizeHtml(html),
  };
}
